import { generalId, PathwayStatus } from '@app/advisor-shared';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_ColorGroup, ModuleFE_Advisor_OrderGroup, ModuleFE_Advisor_Pathway, ModuleFE_Advisor_PathwayState, ModuleFE_Advisor_PathwayTransition, ModuleFE_Advisor_Resource, ModuleFE_RuntimeActiveNavPathway, ModuleFE_RuntimeActiveNavState, ModuleFE_RuntimeFinding, ModuleFE_RuntimeOrder, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { Const_OrderSet, DBDef_Action, DBDef_OrderGroup, TransitionCondition } from '@app/pathway-shared';
import { advisorConfig_ShowOrderSetProtocols } from '../../modules/ModuleFE_AdvisorSettings/types.js';
import { ModuleFE_AdvisorSettings } from '../../modules/ModuleFE_AdvisorSettings/ModuleFE_AdvisorSettings.js';
import { _keys, _values, filterInstances, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { Protocol_DispositionType_Admit, Protocol_DispositionType_Discharge, ProtocolState_Completed, ProtocolStatus_AwaitingDispo, ProtocolStatus_AwaitingInfo, ProtocolStatus_AwaitingTests, ProtocolStatus_Done } from './types.js';
import { ScoreData } from '../ScoreData.js';
import { DiagnosisData } from '../DiagnosisData.js';
import { getProtocolActions } from '../get-protocol-actions.js';
import { getOrderResourceType } from '../get-resource-type.js';
import { getProtocolNextStep } from '../../modules/provide-info/helpers.js';
export class ProtocolData {
    protocol;
    pathway;
    scoreData;
    diagnosisData;
    actions;
    activePathway;
    static modulesToAwait = [
        //Static Modules
        ModuleFE_Advisor_Action,
        ModuleFE_Advisor_ColorGroup,
        ModuleFE_Advisor_OrderGroup,
        ModuleFE_Advisor_Resource,
        ModuleFE_Advisor_Pathway,
        ModuleFE_Advisor_PathwayState,
        ModuleFE_Advisor_PathwayTransition,
        //Runtime Modules
        ModuleFE_RuntimeProtocol,
        ModuleFE_RuntimeFinding,
        ModuleFE_RuntimeOrder,
        ModuleFE_RuntimeActiveNavPathway,
        ModuleFE_RuntimeActiveNavState,
    ];
    static createNew = (protocolId) => {
        if (!protocolId)
            return;
        const protocol = ModuleFE_RuntimeProtocol.cache.unique(protocolId);
        if (!protocol || protocol.pathwayId === generalId)
            return;
        return new ProtocolData(protocol);
    };
    constructor(protocol) {
        const pathway = ModuleFE_Advisor_Pathway.cache.unique(protocol.pathwayId);
        if (!pathway)
            throw new MUSTNeverHappenException(`Could not get pathway for id ${protocol.pathwayId} for protocol with id ${protocol._id}`);
        this.protocol = protocol;
        this.pathway = pathway;
        this.activePathway = ModuleFE_RuntimeActiveNavPathway.cache.find(p => p.pathwayDbId === pathway._id);
        // A protocol can score multiple diseases. The CareMap outcomes (one per
        // disease) are the source of truth for which diagnoses to surface as tabs:
        // `protocol.variableIds` diverges from the outcome keys (both in count and
        // in identity), so deriving tabs from it dropped the extra outcomes and/or
        // rendered an empty tab for a variableId that has no outcome. Use the
        // outcome keys when present, falling back to variableIds for protocols that
        // carry no CareMap outcome (e.g. purely manual / rule protocols).
        const careMapKeys = _keys(protocol.careMapOutcomes ?? {});
        const diagnosisIds = careMapKeys.length ? careMapKeys : protocol.variableIds;
        this.diagnosisData = diagnosisIds.reduce((map, diagnosisId) => {
            map[diagnosisId] = new DiagnosisData(protocol, diagnosisId);
            return map;
        }, {});
        this.scoreData = this.pathway.scoreReferences?.length ? filterInstances(this.pathway.scoreReferences?.map(scoreRef => {
            if (!scoreRef.variableId)
                return;
            return ScoreData.createNew(scoreRef);
        })) : [];
        this.actions = getProtocolActions(this.protocol);
    }
    //######################### Internal Logic #########################
    getHighestCMOScore = () => {
        const cmos = _values(this.protocol.careMapOutcomes ?? {});
        const scores = filterInstances(cmos.map(cmo => cmo?.normalizedScore));
        return Math.max(...scores);
    };
    getCurrentPreconditionIds = () => {
        const activeNavState = this.getActiveState();
        const currentState = ModuleFE_Advisor_PathwayState.cache.unique(activeNavState?.stateDbId);
        if (!currentState)
            return;
        return currentState.preconditions.reduce((ids, precondition) => {
            if (precondition.itemPointer.dbKey === DBDef_Action.dbKey)
                ids.push(precondition.itemPointer.id);
            else if (precondition.itemPointer.dbKey === DBDef_OrderGroup.dbKey) {
                const group = ModuleFE_Advisor_OrderGroup.cache.unique(precondition.itemPointer.id);
                ids.push(precondition.itemPointer.id, ...group?.items.map(item => item.id) ?? []);
            }
            return ids;
        }, []);
    };
    //######################### Public Logic #########################
    static sort = (datas) => {
        if (datas.some(data => data.getLowestSortingIndex() !== Infinity)) {
            return sortArray(datas, data => data.getLowestSortingIndex());
        }
        return sortArray(datas, data => data.getHighestCMOScore(), true);
    };
    isOrderSet = () => this.pathway.type === Const_OrderSet;
    /**
     * Single source of truth for which protocol cards the advisor displays.
     * Order-set protocols are hidden by default (their orders still surface in
     * the Suggested Workup, which is sourced independently from RuntimeOrders);
     * the dev-only `showOrderSetProtocols` toggle reveals the underlying cards.
     */
    static filterDisplayable = (datas) => {
        if (ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_ShowOrderSetProtocols))
            return datas;
        return datas.filter(data => !data.isOrderSet());
    };
    getColor = () => {
        //At the moment return the first color found from the diagnosis
        const diagnosisColors = _values(this.diagnosisData).map(data => data.getColor());
        return filterInstances(diagnosisColors)[0];
    };
    getLabel = () => {
        return this.pathway.label;
    };
    getDiagnosisLabel = () => {
        if (this.pathway.variableIds.length === 1)
            return;
        const diagnosis = _values(this.diagnosisData).map(data => data.diagnosis);
        sortArray(diagnosis, diagnosis => this.protocol.variableIds.indexOf(diagnosis._id));
        return diagnosis.map(diagnosis => diagnosis.name).join('; ');
    };
    getConsideration = () => {
        return this.protocol.activeConsiderationGroup.considerationGroup;
    };
    /**
     * Backend-owned UI list placement (A/B/C/D). The EM ProtocolPlacementModule
     * is the single source of truth; the UI buckets purely by this value and
     * never re-derives placement from status/consideration/orders.
     */
    getListAssignment = () => {
        return this.protocol.listAssignment;
    };
    isDone = () => ModuleFE_RuntimeProtocol.protocolIsDone(this.protocol);
    getDiagnosisData = (diagnosisIds) => {
        if (!diagnosisIds)
            diagnosisIds = this.protocol.variableIds;
        return sortArray(_values(this.diagnosisData).filter(data => !data.isOut()), data => this.protocol.variableIds.indexOf(data.diagnosis._id));
    };
    getStatus = () => {
        if (this.protocol.status === PathwayStatus.ADMIT_COMPLETED || this.protocol.status === PathwayStatus.DISCHARGE_COMPLETED)
            return ProtocolState_Completed;
        if (ModuleFE_RuntimeProtocol.protocolIsDone(this.protocol))
            return ProtocolStatus_Done;
        if (this.actions.disposition.length)
            return ProtocolStatus_AwaitingDispo;
        return this.protocol.neededVariables?.length ? ProtocolStatus_AwaitingInfo : ProtocolStatus_AwaitingTests;
    };
    getAwaitingOrders = () => {
        const awaitingOrders = [];
        const preconditionIds = this.getCurrentPreconditionIds();
        if (this.getStatus() === ProtocolStatus_AwaitingDispo) {
            this.actions.disposition.forEach(action => {
                awaitingOrders.push({
                    label: action === 'admit' ? 'Admit' : 'Discharge',
                    resourceType: action,
                });
            });
        }
        this.actions.groups.forEach(group => {
            //Filter out non-precondition items
            if (preconditionIds && !preconditionIds.includes(group.groupId))
                return;
            const dbGroup = ModuleFE_Advisor_OrderGroup.cache.unique(group.groupId);
            if (!dbGroup)
                return;
            //Filter out group if one of its items is a pre-condition
            if (dbGroup.items.find(item => preconditionIds?.includes(item.id)))
                return;
            const firstDBOrder = ModuleFE_Advisor_Action.cache.unique(dbGroup.items[0].id);
            if (!firstDBOrder)
                return;
            const label = `[${group.runtimeOrders.length}] ${dbGroup.name ?? dbGroup.label}`;
            awaitingOrders.push({
                label,
                resourceType: getOrderResourceType(firstDBOrder)
            });
        });
        this.actions.orders.forEach(order => {
            //Filter out non-precondition items
            if (preconditionIds && !preconditionIds.includes(order.orderId))
                return;
            const dbOrder = ModuleFE_Advisor_Action.cache.unique(order.orderId);
            if (!dbOrder)
                return;
            awaitingOrders.push({
                label: dbOrder.name,
                resourceType: getOrderResourceType(dbOrder)
            });
        });
        return awaitingOrders;
    };
    getActiveState = () => {
        return ModuleFE_RuntimeActiveNavState.cache.unique(this.activePathway?.activeState);
    };
    getCurrentStateLabel = () => {
        const activeState = this.getActiveState();
        const dbState = ModuleFE_Advisor_PathwayState.cache.unique(activeState?.stateDbId);
        return dbState?.label;
    };
    getDispositionType = () => {
        switch (this.protocol.status) {
            case PathwayStatus.ADMIT_COMPLETED:
                return Protocol_DispositionType_Admit;
            case PathwayStatus.DISCHARGE_COMPLETED:
                return Protocol_DispositionType_Discharge;
        }
    };
    getNextGoToSteps = () => {
        const activeState = this.getActiveState();
        if (!activeState?.transitionsConditions)
            return;
        const goToTransitionIds = _keys(activeState.transitionsConditions).filter(id => activeState.transitionsConditions[id] === TransitionCondition.GO_TO_TRANSITION);
        if (goToTransitionIds.length !== 1)
            return;
        return filterInstances(goToTransitionIds.map(getProtocolNextStep));
    };
    getLowestSortingIndex = () => {
        const cmos = _values(this.protocol.careMapOutcomes ?? {});
        const indexes = filterInstances(cmos.map(cmo => cmo?.sortingIndex));
        return Math.min(...indexes);
    };
}
//# sourceMappingURL=ProtocolData.js.map